import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft } from "lucide-react";
import { LogoMark, BRAND } from "@/components/Logo";
import api from "@/lib/api";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      // dev mode returns OTP; in production it would arrive via email.
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success("If that email is registered, an OTP has been sent.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request OTP");
    } finally { setLoading(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: newPw });
      toast.success("Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2.5 mb-4">
          <LogoMark size="sm" />
          <div className="text-base font-semibold text-slate-900">{BRAND.short}</div>
        </div>
        <h1 className="text-2xl tracking-tight font-semibold text-slate-900">
          {step === 1 ? "Forgot your password?" : "Enter your OTP"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {step === 1
            ? "Enter your registered email and we'll send a 6-digit one-time code."
            : `We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
        </p>

        {step === 1 && (
          <form onSubmit={request} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                data-testid="fp-email" type="email" required value={email}
                onChange={(e)=>setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <button data-testid="fp-send" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-blue-700 disabled:opacity-50">
              <Mail className="w-4 h-4" /> {loading ? "Sending…" : "Send OTP"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={reset} className="mt-6 space-y-4">
            {devOtp && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
                <strong>Dev mode:</strong> your OTP is <span className="font-mono-acc font-semibold">{devOtp}</span> (in production this would be emailed).
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">6-digit OTP</label>
              <input data-testid="fp-otp" type="text" inputMode="numeric" maxLength={6} required value={otp}
                onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 tracking-[0.5em] text-lg font-mono-acc focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
              <input data-testid="fp-newpw" type="password" required value={newPw}
                onChange={(e)=>setNewPw(e.target.value)}
                placeholder="8+ chars, upper, lower, number, special"
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <button data-testid="fp-reset" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-blue-700 disabled:opacity-50">
              <KeyRound className="w-4 h-4" /> {loading ? "Resetting…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
